import { Controller } from "@hotwired/stimulus";

const REFRESH_INTERVAL_MS = 30_000;

export default class extends Controller {
  static targets = ["board", "totalCount"];
  static values = {
    refreshUrl: String,
    limit: Number,
    serverNow: Number
  };

  connect() {
    this.connected = true;
    this.connectionGeneration = (this.connectionGeneration || 0) + 1;
    this.refreshTimer = null;
    this.expiryTimer = null;
    this.refreshAbort = null;
    this.refreshEnabled = true;
    this.refreshing = false;
    this.clockOffsetMs = this.hasServerNowValue ? this.serverNowValue - Date.now() : 0;
    this.scheduleExpiry();
    this.scheduleRefresh();
  }

  disconnect() {
    this.connected = false;
    this.connectionGeneration += 1;
    window.clearTimeout(this.refreshTimer);
    window.clearTimeout(this.expiryTimer);
    this.refreshAbort?.abort();
    this.refreshAbort = null;
  }

  refresh() {
    this.scheduleExpiry();
  }

  async fetchBoard() {
    window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    if (!this.connected || !this.refreshEnabled) return;
    if (this.refreshing || !this.hasRefreshUrlValue) {
      this.scheduleRefresh();
      return;
    }

    this.refreshing = true;
    const connectionGeneration = this.connectionGeneration;
    const abortController = new AbortController();
    this.refreshAbort = abortController;
    try {
      const url = new URL(this.refreshUrlValue, window.location.origin);
      if (this.hasLimitValue) url.searchParams.set("limit", String(this.limitValue));

      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: abortController.signal
      });
      if (!this.refreshIsCurrent(connectionGeneration, abortController)) return;
      const contentType = response.headers.get("content-type") || "";
      if (response.status === 401 || response.status === 403 || !contentType.includes("application/json")) {
        this.refreshEnabled = false;
        return;
      }
      if (!response.ok) throw new Error("Request wire refresh failed");

      const payload = await response.json();
      if (!this.refreshIsCurrent(connectionGeneration, abortController)) return;
      if (this.hasBoardTarget && typeof payload.board_html === "string") {
        this.boardTarget.innerHTML = payload.board_html;
      }
      if (this.hasTotalCountTarget && Number.isFinite(Number(payload.total_count))) {
        this.totalCountTarget.textContent = String(payload.total_count);
      }
      if (Number.isFinite(Number(payload.server_now_ms))) {
        this.clockOffsetMs = Number(payload.server_now_ms) - Date.now();
      }
      this.scheduleExpiry();
    } catch (error) {
      if (error.name === "AbortError" || !this.refreshIsCurrent(connectionGeneration, abortController)) return;
      this.pruneExpired();
    } finally {
      if (this.refreshIsCurrent(connectionGeneration, abortController)) {
        this.refreshing = false;
        if (this.refreshAbort === abortController) this.refreshAbort = null;
        this.scheduleRefresh();
      }
    }
  }

  refreshIsCurrent(connectionGeneration, abortController) {
    return this.connected &&
      this.connectionGeneration === connectionGeneration &&
      !abortController.signal.aborted;
  }

  expireAndRefresh() {
    this.pruneExpired();
    this.fetchBoard();
  }

  pruneExpired() {
    if (!this.hasBoardTarget) return;

    const now = this.serverTimeMs();
    const items = Array.from(this.boardTarget.querySelectorAll("[data-longplay-request-expires-at-ms]"));
    let removed = 0;

    items.forEach((item) => {
      const expiresAt = Number(item.dataset.longplayRequestExpiresAtMs);
      if (Number.isFinite(expiresAt) && expiresAt <= now) {
        item.remove();
        removed += 1;
      }
    });

    if (removed > 0 && this.hasTotalCountTarget) {
      const oldTotal = Number.parseInt(this.totalCountTarget.textContent, 10) || 0;
      this.totalCountTarget.textContent = String(Math.max(0, oldTotal - removed));
    }
    if (items.length > 0 && removed === items.length) {
      this.boardTarget.innerHTML = '<p class="longplay-request-empty">No requests in the last 30 minutes. Be the next signal.</p>';
    }

    this.scheduleExpiry();
  }

  scheduleExpiry() {
    window.clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    if (!this.connected || !this.hasBoardTarget) return;

    const expirations = Array.from(this.boardTarget.querySelectorAll("[data-longplay-request-expires-at-ms]"))
      .map((item) => Number(item.dataset.longplayRequestExpiresAtMs))
      .filter(Number.isFinite);
    if (expirations.length === 0) return;

    const nextExpiry = Math.min(...expirations);
    const delay = Math.max(25, nextExpiry - this.serverTimeMs() + 25);
    this.expiryTimer = window.setTimeout(() => this.expireAndRefresh(), delay);
  }

  scheduleRefresh() {
    window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    if (!this.connected || !this.refreshEnabled) return;
    this.refreshTimer = window.setTimeout(() => this.fetchBoard(), REFRESH_INTERVAL_MS);
  }

  serverTimeMs() {
    return Date.now() + this.clockOffsetMs;
  }
}
